import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import type { ApiError } from "../errors";
import { billingApi } from "../endpoints";
import { qk } from "../query-keys";
import type {
  CheckoutOut,
  CheckoutRequest,
  MockCompleteRequest,
  PaymentModeOut,
  PlanOut,
  ProDemoData,
  SubscriptionOut,
  WebhookResult,
} from "../types";

type QueryOpts<T> = Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn">;

/**
 * Current subscription. `effective_plan` (not `plan`) decides Pro access:
 * a tenant on the free plan still gets Pro while the 14-day trial is running.
 */
export function useSubscription(options?: QueryOpts<SubscriptionOut>) {
  return useQuery({
    queryKey: qk.subscription,
    queryFn: () => billingApi.subscription(),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** 可售卖的计费档位（pro_monthly / pro_yearly），运营改价后自动跟随。 */
export function useBillingPlans(options?: QueryOpts<PlanOut[]>) {
  return useQuery({
    queryKey: qk.billingPlans,
    queryFn: () => billingApi.plans(),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** 当前支付模式：`mock` 时前端只提示沙箱，不跳转外部收银台。 */
export function usePaymentMode(options?: QueryOpts<PaymentModeOut>) {
  return useQuery({
    queryKey: qk.paymentMode,
    queryFn: () => billingApi.paymentMode(),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** 创建收银台。开通要等渠道 webhook 回来，所以这里只返回审批链接/沙箱单号。 */
export function useCreateCheckout() {
  return useMutation<CheckoutOut, ApiError, CheckoutRequest>({
    mutationFn: (body) => billingApi.createCheckout(body),
  });
}

/** 取消订阅：当期权益保留到周期结束，到期由 cron 降级。 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation<SubscriptionOut, ApiError, void>({
    mutationFn: () => billingApi.cancelSubscription(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.subscription });
    },
  });
}

/** 沙箱支付完成（本地演示）：开通后订阅与租户权益都需要重取。 */
export function useMockComplete() {
  const queryClient = useQueryClient();
  return useMutation<WebhookResult, ApiError, MockCompleteRequest>({
    mutationFn: (body) => billingApi.mockComplete(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.subscription });
      void queryClient.invalidateQueries({ queryKey: qk.me });
    },
  });
}

/**
 * Pro-gated endpoint. Pass `enabled: false` for free/expired tenants to avoid a
 * guaranteed 402, or leave it on to render the upgrade wall from the error.
 */
export function useProDemo(options?: QueryOpts<ProDemoData>) {
  return useQuery({
    queryKey: qk.proDemo,
    queryFn: () => billingApi.proDemo(),
    retry: false,
    staleTime: 5 * 60_000,
    ...options,
  });
}
